import { useTranslation } from 'react-i18next';
import DashboardBlock from '../DashboardBlock/DashboardBlock';
import styles from './OrderBarChart.module.scss';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import SpinnerCircle from '~/ui/SpinnerCircle/SpinnerCircle';
import { Button, Spinner } from '~/ui';
import { useEffect, useState } from 'react';
import { NextPrevChartNames } from '~/utils/types/dashboard';
import { CurrentBar } from './CurrentBar/CurrentBar';
import { GeneralBar } from './GeneralBar/GeneralBar';
import { NextPrevDataLoadBtn } from '~/ui/NextPrevChartBtn/NextPrevDataLoadBtn';

export interface IOrderDataProps {
  all_count: number;
  total_pages: number;
  statistic: {
    [key: string]: {
      all_count: number;
      statuses: {
        cancel_by_admin: number;
        cancel_by_system: number;
        cancel_by_user: number;
        canceled: number;
        created: number;
        success: number;
      };
    };
  };
}

export interface ICurrentData {
  label: string;
  data: {
    all_count: number;
    statistic: {
      canceled: number;
      created: number;
      success: number;
      cancel_by_admin: number;
      cancel_by_system: number;
      cancel_by_user: number;
    };
  };
}

interface IOrderBarProps {
  startDate: string;
  endDate: string;
  assetsIds: [] | number[];
  accommodation_ids: number[];
  organization_ids: number[];
}

const OrderBarChart = ({
  startDate,
  endDate,
  assetsIds,
  accommodation_ids,
  organization_ids,
}: IOrderBarProps) => {
  const [selectedData, setSelectedData] = useState<null | string>(null);
  const [currentData, setCurrentData] = useState<ICurrentData[]>([]);
  const [isCurrentData, setIsCurrentData] = useState(false);
  const [page, setPage] = useState(1);
  const { t } = useTranslation();
  const toastError = useToastError();

  const handleReset = () => {
    setSelectedData(null);
    setCurrentData([]);
    setIsCurrentData(false);
    setPage(1);
  };

  const params = {
    time: [startDate, endDate],
    asset_ids: assetsIds,
    accommodation_ids,
    organization_ids,
    page,
    per_page: 5,
  };

  const { data: orderData, isLoading } = useQuery<IOrderDataProps>({
    queryFn: async () => {
      const { data } = await instance.get('admin/dashboard/orders', { params });
      return data;
    },
    queryKey: ['dashboard-orders', params],
    onError: error => {
      toastError(error);
    },
    enabled: !!startDate && !!endDate,
  });

  const handleNextPrev = (value: NextPrevChartNames) => {
    if (value === NextPrevChartNames.NEXT) {
      setPage(page + 1);
    } else if (value === NextPrevChartNames.PREV) {
      setPage(page - 1);
    }
  };

  useEffect(() => {
    if (selectedData === null && currentData.length === 0) return;
    setIsCurrentData(true);
  }, [currentData, selectedData]);

  useEffect(() => {
    setPage(1);
    setSelectedData(null);
    setCurrentData([]);
    setIsCurrentData(false);
  }, [startDate, endDate]);

  return (
    <DashboardBlock
      title={t('orders')}
      actions={
        <div className={styles.actionsWrapper}>
          <div className={styles.orderValuesWrapper}>
            {isCurrentData && (
              <>
                <Button
                  text={t('back')}
                  type="button"
                  onClick={handleReset}
                  className={styles.backBtn}
                />
                <span className={styles.selectedData}>{selectedData}</span>
              </>
            )}
            <span className={styles.orderTitle}>{t('total_orders')}: </span>
            {orderData ? (
              <span className={styles.orderValue}>{orderData?.all_count}</span>
            ) : (
              <SpinnerCircle />
            )}
          </div>
          <NextPrevDataLoadBtn
            isLoading={isLoading}
            isCurrentData={isCurrentData}
            page={page}
            totalPages={orderData?.total_pages}
            onClick={handleNextPrev}
          />
        </div>
      }
    >
      {orderData || !isLoading ? (
        isCurrentData === false ? (
          <GeneralBar
            orderData={orderData}
            setSelectedData={setSelectedData}
            setCurrentData={setCurrentData}
          />
        ) : (
          <CurrentBar currentData={currentData} />
        )
      ) : (
        <div className={styles.spinnerWrapper}>
          <Spinner />
        </div>
      )}
    </DashboardBlock>
  );
};

export default OrderBarChart;
